/**
 * RecipeCard — library landing card (R2 library-surface redesign).
 *
 * Rewritten to the committed comp "Option 2" (stat card) anatomy:
 *   `.plan/comps/library-comps.html` (Option 2 section) — the frozen build spec
 *   for type sizes, spacing, and honest states.
 *   `.plan/features/2026-07-03-library-surface-redesign.md` — pick rationale.
 *
 * Anatomy (comp Option 2):
 *   - Head row: 40px emoji tile + title (+ optional accent draft dot, optional
 *     lineage sub-caption).
 *   - Hairline `border-top` stat block: a 2-column grid of cells — muted sans
 *     labels (`go-to`, `rating`, `cooks`, `variations`) with mono tabular-num
 *     values (`v12`, `★ 4.5`, `18`, `3`) — then a full-width mono footer
 *     (`cooked 5d ago`).
 *   - Card chrome: --surface bg, 1px --line border, radius 12, padding 16.
 *
 * Honest states (§2a discipline — comp-specified, review-rejectable if violated):
 *   - A recipe whose `stats` field is ABSENT renders the lean card: identity +
 *     mono `edited Nd ago` footer, NO stat grid.
 *   - A recipe with ZERO cooks renders identically to the stats-absent lean card
 *     (no stat grid at all — never a literal "0 cooks").
 *   - Unrated recipe (rating null/0) omits the `rating` cell entirely — never
 *     "★ 0.0".
 *   - `goToVersion` null/0 omits the `go-to` cell entirely.
 *   - `lastCookedAtMs` null falls the footer back to `edited …`.
 *   - No loading spinners on cards.
 *
 * Design refs:
 *   design.md §2 — accent reserved for signal (here: the draft dot + the
 *     active-state border only); hairlines only, no shadow; 200 ms ease.
 *   design.md §4 — tokens: --bg, --surface, --text, --muted, --faint, --line,
 *     --accent. Mono ONLY for values + footer; sans for labels + title.
 *   design.md §15.3 — 44×44 min tap target (the whole card is the target).
 *   branding.md §8 — go-to (not "prod"), Variation (not "branch"), Spin-off
 *     (not "fork"). branding.md §5.2.1 — no banned words, no exclamations.
 *
 * Emoji key visual:
 *   - System font stack: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji'.
 *   - Fallback: '🍳' when recipe.emoji is absent.
 *   - The emoji is an inert presentation glyph on the library card — it is NOT a
 *     tap target. The whole card press opens the recipe (operator 2026-07-02:
 *     the library emoji-edit trigger was an accidental tap target). Changing the
 *     emoji lives ONLY on the recipe viewer page (RecipeHeader).
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 */

import { memo, useRef, useEffect, useState } from "react";
import type React from "react";
import type { Recipe } from "~/features/explorer/types";
// A-WP3 — feature photo on the library card. In-app display uses the session-
// minted mintGetUrl path (not the anonymous public route). Falls back to the
// emoji tile when unset, still loading, or the image fails to load.
import { useMediaImage } from "~/features/media/useMediaUrl";
// Relative-time: the terse `Nd ago` / `Nmo ago` idiom the comp footer uses,
// matching the existing helper feeding VariationSwitcherSheet-style last-cook
// lines. Reused (not re-written) so the app keeps one relative-time vocabulary.
import { formatShortRelative } from "~/features/groups/time";
// PERF Wave 3a — F4: high-priority chunk prefetch on card hover/focus.
import { prefetchChunksOnIntent } from "~/lib/prefetch-chunks";

// ─── Constants ────────────────────────────────────────────────────────────────

const FALLBACK_EMOJI = "🍳";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRating(rating: number): string {
  return rating.toFixed(1);
}

// ─── CSS injection ────────────────────────────────────────────────────────────

const CARD_CSS = `
.proof-recipe-card {
  position: relative;
  display: block;
  padding: 16px;
  background: var(--surface, #fdfcfa);
  border: 1px solid var(--line, #dcd7cc);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 200ms ease, background 200ms ease;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px;
  text-align: left;
  width: 100%;
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
}

.proof-recipe-card:hover {
  border-color: var(--line-hover, #D0CCC5);
  background: var(--surface-raised, #FEFCF9);
}

.proof-recipe-card:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}

/* Head row — emoji tile + title/lineage. */
.proof-recipe-card__head {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
}

/* Emoji tile is an inert presentation glyph on the library card (not a tap
   target). Changing the emoji lives only on the recipe viewer page. */
.proof-recipe-card__emoji-wrap {
  flex: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--surface, #fdfcfa);
  border: 1px solid var(--line, #dcd7cc);
  font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  font-size: 20px;
  line-height: 1;
  user-select: none;
}

.proof-recipe-card__main {
  flex: 1 1 0;
  min-width: 0;
}

.proof-recipe-card__title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-top: 1px;
}

.proof-recipe-card__title {
  font-size: 15px;
  font-weight: 500;
  color: var(--text, #1a1714);
  letter-spacing: -0.01em;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
  min-width: 0;
}

/* Draft dot — the only accent on the card head (design.md §2 signal use).
   Pushed to the far end of the title row. */
.proof-recipe-card__draft-dot {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent, #c23d12);
  margin-left: auto;
}

.proof-recipe-card__lineage {
  font-size: 12.5px;
  color: var(--faint, #766d60);
  letter-spacing: 0;
  line-height: 1.4;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Stat block — hairline-topped 2-col grid. Labels muted sans, values mono. */
.proof-recipe-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 20px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--line, #dcd7cc);
}

.proof-recipe-card__cell {
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-size: 12px;
  min-width: 0;
}

.proof-recipe-card__cell-label {
  color: var(--muted, #71685b);
}

.proof-recipe-card__cell-value {
  font-family: var(--font-mono, 'Geist Mono', 'Courier New', monospace);
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  color: var(--text, #1a1714);
  white-space: nowrap;
}

/* Full-width mono recency footer inside the stat grid. */
.proof-recipe-card__footer {
  grid-column: 1 / -1;
  font-family: var(--font-mono, 'Geist Mono', 'Courier New', monospace);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--faint, #766d60);
}

/* Lean-card recency footer — no stat grid above it (zero cooks / stats absent). */
.proof-recipe-card__lean-footer {
  font-family: var(--font-mono, 'Geist Mono', 'Courier New', monospace);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--faint, #766d60);
  margin-top: 14px;
}
`;

if (typeof document !== "undefined") {
  const id = "proof-recipe-card-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = CARD_CSS;
    document.head.appendChild(el);
  }
}

// ─── Key visual (feature photo or emoji tile) ──────────────────────────────────

/**
 * The 40px key-visual tile: the feature photo when set + ready, else the emoji
 * glyph (then the browser's emoji fallback). An inert presentation element — not
 * a tap target (the whole card press opens the recipe). §2a: no reserved space
 * or fabricated state — an unset recipe renders byte-identically to before.
 */
function RecipeCardKeyVisual({
  uuid,
  emoji,
  featureMediaUuid,
}: {
  uuid: string;
  emoji: string;
  featureMediaUuid?: string | null | undefined;
}) {
  // Hook is called unconditionally (rules-of-hooks); a null uuid resolves to null.
  // useMediaImage exposes onImageError → one-shot re-mint, closing the 5-min
  // token-TTL window that lazy loading opened (a card scrolled into view after
  // the token expired would otherwise 403 straight to the emoji fallback).
  const { url, onImageError } = useMediaImage(featureMediaUuid ?? null, "thumb-256", {
    ready: true,
    width: 80,
  });
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);

  const showPhoto = Boolean(featureMediaUuid) && Boolean(url) && !broken;

  return (
    <span
      className="proof-recipe-card__emoji-wrap"
      data-testid={`recipe-card-emoji-${uuid}`}
      aria-hidden="true"
      style={showPhoto ? { padding: 0, overflow: "hidden" } : undefined}
    >
      {showPhoto ? (
        <img
          src={url ?? undefined}
          alt=""
          // PERF (recipe-switch wave): defer off-screen feature-photo fetch +
          // async decode so a long library never blocks paint on image work. No
          // CLS: the img fills a fixed 40x40 tile (width/height 100% of the
          // emoji-wrap), so lazy loading cannot shift layout.
          loading="lazy"
          decoding="async"
          data-testid={`recipe-card-feature-photo-${uuid}`}
          // One-shot re-mint on the first load error (expired token); only fall
          // back to the emoji tile once the single re-mint budget is spent.
          onError={() => {
            if (!onImageError()) setBroken(true);
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        emoji
      )}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface RecipeCardProps {
  recipe: Recipe;
  isActive: boolean;
  onClick: (uuid: string) => void;
  /**
   * PERF Wave 3a — F4: optional data-prefetch callback.
   *
   * Called after a sustained hover (~120 ms) on the card. The parent wires
   * this to `cachedApi.recipe.get(uuid)` so the SWR cache is warm by the time
   * the user clicks (mirrors the FlatRecipeList.onPrefetchRecipe pattern from
   * PR #168). Optional so call sites that haven't wired it compile unchanged.
   */
  onPrefetchRecipe?: (uuid: string) => void;
}

function RecipeCardImpl({ recipe, isActive, onClick, onPrefetchRecipe }: RecipeCardProps) {
  // ── Hover / focus handlers (F4) ─────────────────────────────────────────
  // Chunk prefetch fires immediately on pointerenter / focus (high priority:
  // the user has expressed intent). Data prefetch is debounced at 120 ms
  // (mirrors FlatRecipeList) to filter fly-through scroll passes.
  //
  // `(pointer: fine)` gate for the data prefetch matches PR #168 behaviour:
  // touch pointerenter is unreliable and the click is milliseconds away.
  // Chunk prefetch also skips coarse-pointer to avoid mid-tap network calls.
  //
  // prefetchChunksOnIntent() is idempotent and module-level-guarded — calling
  // it on every card hover is safe; only the first effective call downloads.
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefetchFiredRef = useRef(false);

  function handlePointerEnter(e: React.PointerEvent) {
    // Gate: fine pointer only (mouse/stylus). Touch pointerenter is unreliable.
    if (e.pointerType !== "mouse" && e.pointerType !== "pen") return;
    // Chunk prefetch — immediate, high priority.
    prefetchChunksOnIntent();
    // Data prefetch — debounced 120 ms to filter scroll fly-through.
    if (!onPrefetchRecipe || prefetchFiredRef.current) return;
    if (prefetchTimerRef.current !== null) {
      clearTimeout(prefetchTimerRef.current);
    }
    prefetchTimerRef.current = setTimeout(() => {
      prefetchTimerRef.current = null;
      if (prefetchFiredRef.current) return;
      prefetchFiredRef.current = true;
      onPrefetchRecipe(recipe.uuid);
    }, 120);
  }

  function handlePointerLeave() {
    if (prefetchTimerRef.current !== null) {
      clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }
  }

  function handlePointerDown() {
    // PERF: `pointerdown` precedes `click` on every input type — this covers
    // touch taps (where the fine-pointer gate above never fires) and fast
    // clicks that land inside the 120 ms hover debounce. The activation is
    // milliseconds away, so fire chunk + data prefetch immediately; the SWR
    // layer and module loader dedupe against the click's own fetches.
    prefetchChunksOnIntent();
    if (!onPrefetchRecipe || prefetchFiredRef.current) return;
    if (prefetchTimerRef.current !== null) {
      clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }
    prefetchFiredRef.current = true;
    onPrefetchRecipe(recipe.uuid);
  }

  function handleFocus() {
    // Focus (keyboard navigation) also signals intent — fire chunk prefetch
    // immediately. No data-prefetch debounce on focus (keyboard nav is slow).
    prefetchChunksOnIntent();
    if (!onPrefetchRecipe || prefetchFiredRef.current) return;
    prefetchFiredRef.current = true;
    onPrefetchRecipe(recipe.uuid);
  }

  // Cancel any pending data-prefetch timer on unmount so a late-firing
  // setTimeout does not invoke onPrefetchRecipe on a torn-down card.
  useEffect(() => {
    return () => {
      if (prefetchTimerRef.current !== null) {
        clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = null;
      }
    };
  }, []);

  const emoji = recipe.emoji ?? FALLBACK_EMOJI;
  const uuid = recipe.uuid;

  // ── Stat derivation (comp Option 2, honest-state gated) ───────────────────
  // The card is either the LEAN form (identity + `edited Nd ago`, no stat grid)
  // or the STAT form (hairline-topped 2-col grid + `cooked Nd ago`). It renders
  // the stat grid ONLY when `stats` is present AND there is at least one cook —
  // a zero-cook recipe and a stats-absent recipe are visually identical (§2a:
  // never a fabricated "0 cooks" / "★ 0.0"). Within the grid, each cell is
  // independently gated.
  const stats = recipe.stats;
  const showStatGrid = stats !== undefined && stats.cooks > 0;

  // go-to cell — only when goToVersion is a positive number (null/0 → omit).
  const showGoTo = showStatGrid && typeof stats?.goToVersion === "number" && stats.goToVersion > 0;
  // rating cell — only when rating is a positive number (null/0 → omit; no ★ 0.0).
  const showRating = showStatGrid && typeof stats?.rating === "number" && stats.rating > 0;

  // Recency footer text. Stat form → `cooked Nd ago` from lastCookedAtMs (falls
  // back to `edited …` from lastEditedAt if the cook timestamp is missing).
  // Lean form → `edited Nd ago` from lastEditedAt.
  const cookedRel =
    stats !== undefined && stats.lastCookedAtMs !== null
      ? formatShortRelative(stats.lastCookedAtMs)
      : null;
  const editedRel = formatShortRelative(recipe.lastEditedAt);
  const footerText =
    showStatGrid && cookedRel !== null && cookedRel !== ""
      ? `cooked ${cookedRel}`
      : `edited ${editedRel}`;

  // Whole-card "open recipe" lives on a <div role="button"> with Enter/Space
  // keyboard parity — the established per-row-action pattern from FlatRecipeList.
  // (The emoji tile is an inert glyph, so there is no nested control.)
  function handleCardKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Guard against activations bubbled from any nested control (defensive).
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(uuid);
    }
  }

  return (
    <div
      className="proof-recipe-card"
      data-testid={`recipe-card-${uuid}`}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={recipe.title}
      onClick={() => onClick(uuid)}
      onKeyDown={handleCardKeyDown}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerDown={handlePointerDown}
      onFocus={handleFocus}
      style={
        isActive
          ? {
              borderColor: "var(--accent, #c23d12)",
              background: "color-mix(in srgb, var(--accent, #c23d12) 4%, var(--surface, #fdfcfa))",
            }
          : undefined
      }
    >
      {/* Head row — emoji tile + title/lineage. */}
      <div className="proof-recipe-card__head">
        {/*
         * Key visual — the feature photo when set (A-WP3), else the emoji glyph.
         * An inert presentation element (not a tap target). The whole card press
         * opens the recipe. Changing the emoji / feature photo lives only on the
         * recipe viewer page (operator 2026-07-02).
         */}
        <RecipeCardKeyVisual uuid={uuid} emoji={emoji} featureMediaUuid={recipe.featureMediaUuid} />

        <div className="proof-recipe-card__main">
          {/* Title row — title + optional draft dot. */}
          <div className="proof-recipe-card__title-row">
            <span className="proof-recipe-card__title" data-testid={`recipe-card-title-${uuid}`}>
              {recipe.title}
            </span>

            {/*
             * §2a: draft dot ONLY when hasDraft === true. No element, no reserved
             * space when false. Ember accent — the current-work signal (design.md §2).
             */}
            {recipe.hasDraft === true && (
              <span
                className="proof-recipe-card__draft-dot"
                data-testid={`recipe-card-draft-dot-${uuid}`}
                aria-label="Draft in progress"
                role="status"
              />
            )}
          </div>

          {/*
           * §2a: lineage sub-caption ONLY when lineage is present. The string is
           * pre-formatted adaptively by the caller (routes/app.tsx
           * deriveLibraryCardLineage): "Based on <name>" for a self-duplicate,
           * "Spun off from @handle/<slug>" for an adopted share
           * (nav-chrome-contextual-create §4.4). branding.md §8: Spin-off not
           * "fork".
           */}
          {recipe.lineage !== undefined && (
            <span
              className="proof-recipe-card__lineage"
              data-testid={`recipe-card-lineage-${uuid}`}
            >
              {recipe.lineage}
            </span>
          )}
        </div>
      </div>

      {showStatGrid ? (
        /*
         * Stat block — hairline-topped 2-col grid (comp Option 2). Cells in comp
         * order: go-to · rating · cooks, then a full-width footer. Each cell is
         * §2a-gated; omitting one lets the others flow up (matching the comp's
         * rating-less cards). Values are mono + tabular-nums; labels are muted
         * sans. (Variations cell removed 2026-07-09 — versions-first-header.)
         */
        <div className="proof-recipe-card__stats" data-testid={`recipe-card-stats-${uuid}`}>
          {showGoTo && (
            <div className="proof-recipe-card__cell" data-testid={`recipe-card-goto-${uuid}`}>
              <span className="proof-recipe-card__cell-label">go-to</span>
              <span className="proof-recipe-card__cell-value">v{stats?.goToVersion}</span>
            </div>
          )}
          {showRating && (
            <div
              className="proof-recipe-card__cell"
              data-testid={`recipe-card-rating-${uuid}`}
              aria-label={`Rating: ${formatRating(stats?.rating ?? 0)} out of 5`}
            >
              <span className="proof-recipe-card__cell-label">rating</span>
              <span className="proof-recipe-card__cell-value">
                ★ {formatRating(stats?.rating ?? 0)}
              </span>
            </div>
          )}
          <div className="proof-recipe-card__cell" data-testid={`recipe-card-cooks-${uuid}`}>
            <span className="proof-recipe-card__cell-label">cooks</span>
            <span className="proof-recipe-card__cell-value">{stats?.cooks}</span>
          </div>
          <div className="proof-recipe-card__footer" data-testid={`recipe-card-footer-${uuid}`}>
            {footerText}
          </div>
        </div>
      ) : (
        /*
         * Lean card — no stat grid (stats absent OR zero cooks). Just a mono
         * `edited Nd ago` footer. §2a: never a fabricated "0 cooks" / "★ 0.0".
         */
        <div className="proof-recipe-card__lean-footer" data-testid={`recipe-card-footer-${uuid}`}>
          {footerText}
        </div>
      )}
    </div>
  );
}

/**
 * Memoised card. PERF (recipe-switch wave): an unrelated app.tsx re-render (e.g.
 * a `currentDocKey` change on activation, a background revalidation) otherwise
 * re-renders every card in the grid. Props are memo-friendly under the default
 * shallow compare: `recipe` object identity is stable between `recipes` re-maps,
 * `isActive` is a primitive, and LibraryGridView passes referentially-stable
 * `onClick` / `onPrefetchRecipe` wrappers (ref-mirrored useCallback) so the two
 * function props do not defeat the memo. Pure render-cost reduction — no
 * behavioural or visual change.
 */
export const RecipeCard = memo(RecipeCardImpl);
